import React, { useState, useEffect, useCallback, useMemo } from 'react';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SettingsIcon from '@mui/icons-material/Settings';
import SourceIcon from '@mui/icons-material/Link';
import { getJournalEntriesV2, getJournalAccountsV2 } from '../../../api/journal';
import { useFirebase } from '../../../context/FirebaseContext';
import { useTradeHistoryWebhookEnabled } from '../../../context/FeatureFlagContext';
import TradeHistoryWebhookModal from './TradeHistoryWebhookModal';
import './TradeHistoryPanel.css';

// Storage key for persisting filters
const FILTERS_STORAGE_KEY = 'tradeHistoryFilters';

// Default filters
const DEFAULT_FILTERS = {
    period: 'today',      // 'today' or '7d'
    accountId: 'all',     // 'all' or specific account_id
    sourceId: 'all'       // 'all' or specific source_id
};

// Load filters from localStorage
const loadFiltersFromStorage = () => {
    try {
        const stored = localStorage.getItem(FILTERS_STORAGE_KEY);
        if (stored) {
            return { ...DEFAULT_FILTERS, ...JSON.parse(stored) };
        }
    } catch (e) {
        console.warn('Failed to load trade history filters from storage:', e);
    }
    return DEFAULT_FILTERS;
};

// Save filters to localStorage
const saveFiltersToStorage = (filters) => {
    try {
        localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
    } catch (e) {
        console.warn('Failed to save trade history filters to storage:', e);
    }
};

const TradeHistoryPanel = ({ onViewAll, compact = false, limit = 5, refreshTrigger = 0, isExpanded, onToggle, selectedInstrument }) => {
    const { auth } = useFirebase();
    const webhookFeatureEnabled = useTradeHistoryWebhookEnabled();
    const [allEntries, setAllEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [internalCollapsed, setInternalCollapsed] = useState(true);
    const [expandedEntry, setExpandedEntry] = useState(null);
    const [accounts, setAccounts] = useState([]);
    const [webhookModalOpen, setWebhookModalOpen] = useState(false);
    const [filters, setFilters] = useState(loadFiltersFromStorage);

    // Use controlled state if provided (accordion mode), otherwise use internal state
    const collapsed = isExpanded !== undefined ? !isExpanded : internalCollapsed;

    // Toggle function - use controlled if provided, otherwise use internal state
    const handleToggle = () => {
        if (onToggle) {
            // Controlled mode (accordion)
            onToggle();
        } else {
            // Internal state mode (backward compatibility)
            setInternalCollapsed(!internalCollapsed);
        }
    };

    // Handle filter changes and persist to localStorage
    const handleFilterChange = useCallback((filterKey, value) => {
        setFilters(prev => {
            const newFilters = { ...prev, [filterKey]: value };
            saveFiltersToStorage(newFilters);
            return newFilters;
        });
    }, []);

    const fetchEntries = useCallback(async () => {
        if (!auth?.currentUser) return;

        setLoading(true);
        try {
            // Fetch all entries without method_analytics filter
            await getJournalEntriesV2(auth, (result) => {
                if (result && !result.error) {
                    setAllEntries(result);
                }
                setLoading(false);
            }, 1000, 0, 'all', null);
        } catch (error) {
            console.error('Error fetching journal entries:', error);
            setLoading(false);
        }
    }, [auth]);

    // Filter entries based on current filters
    const filteredEntries = useMemo(() => {
        let result = [...allEntries];

        // Period filter
        const now = new Date();
        let cutoff = new Date();

        if (filters.period === 'today') {
            cutoff.setHours(0, 0, 0, 0);
        } else if (filters.period === '7d') {
            cutoff.setDate(now.getDate() - 7);
        }

        result = result.filter(e => new Date(e.trade_date) >= cutoff);

        // Account filter
        if (filters.accountId !== 'all') {
            result = result.filter(e => e.account_id === parseInt(filters.accountId));
        }

        // Source filter
        if (filters.sourceId !== 'all') {
            result = result.filter(e => e.source_id === filters.sourceId);
        }

        return result;
    }, [allEntries, filters]);

    // Get entries to display (limited)
    const entries = useMemo(() => {
        return filteredEntries.slice(0, limit);
    }, [filteredEntries, limit]);

    // Calculate stats from filtered entries
    const stats = useMemo(() => {
        const wins = filteredEntries.filter(e => e.profit_loss > 0).length;
        const losses = filteredEntries.filter(e => e.profit_loss < 0).length;
        const totalPL = filteredEntries.reduce((sum, e) => sum + (e.profit_loss || 0), 0);

        return {
            total: filteredEntries.length,
            wins,
            losses,
            totalPL
        };
    }, [filteredEntries]);

    // Get source IDs available for current period (for dynamic filter options)
    const sourceIdsForPeriod = useMemo(() => {
        let result = [...allEntries];

        const now = new Date();
        let cutoff = new Date();

        if (filters.period === 'today') {
            cutoff.setHours(0, 0, 0, 0);
        } else if (filters.period === '7d') {
            cutoff.setDate(now.getDate() - 7);
        }

        result = result.filter(e => new Date(e.trade_date) >= cutoff);

        const unique = [...new Set(result.map(e => e.source_id).filter(Boolean))];
        return unique.sort();
    }, [allEntries, filters.period]);

    useEffect(() => {
        fetchEntries();
    }, [fetchEntries, refreshTrigger]);

    // Load accounts for filters and webhook settings
    const loadAccounts = useCallback(async () => {
        if (!auth?.currentUser) return;
        try {
            await getJournalAccountsV2(auth, (result) => {
                if (result && !result.error) {
                    setAccounts(result);
                }
            });
        } catch (error) {
            console.error('Error fetching accounts:', error);
        }
    }, [auth]);

    // Always load accounts (needed for filter, not just webhooks)
    useEffect(() => {
        loadAccounts();
    }, [loadAccounts]);

    const handleSettingsClick = (e) => {
        e.stopPropagation();
        setWebhookModalOpen(true);
    };

    // Parse lessons JSON to get validation data
    const parseValidationData = (lessonsStr) => {
        if (!lessonsStr) return null;
        try {
            const data = JSON.parse(lessonsStr);
            // Check if it's the new structured format
            if (data.validationStatus || data.gates) {
                return data;
            }
            // Old format - just an array of gates
            if (Array.isArray(data)) {
                return { gates: data };
            }
            return null;
        } catch {
            return null;
        }
    };

    const formatDate = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays}d ago`;

        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const formatPL = (value) => {
        const num = parseFloat(value) || 0;
        const sign = num >= 0 ? '+' : '';
        return `${sign}$${Math.abs(num).toFixed(2)}`;
    };

    if (loading && allEntries.length === 0) {
        return (
            <div className="trade-history-panel">
                <div className="history-header-row">
                    <div className="history-header" onClick={handleToggle}>
                        <MenuBookIcon fontSize="small" />
                        <span className="header-title">Recent Trades</span>
                        <span className="header-loading">Loading...</span>
                    </div>
                    {webhookFeatureEnabled && (
                        <button
                            className="history-settings-btn"
                            onClick={handleSettingsClick}
                            title="Webhook Settings"
                        >
                            <SettingsIcon fontSize="small" />
                        </button>
                    )}
                </div>
                <TradeHistoryWebhookModal
                    open={webhookModalOpen}
                    onClose={() => setWebhookModalOpen(false)}
                    accounts={accounts}
                    selectedInstrument={selectedInstrument}
                    onAccountUpdate={loadAccounts}
                />
            </div>
        );
    }

    if (allEntries.length === 0) {
        return (
            <div className="trade-history-panel">
                <div className="history-header-row">
                    <div className="history-header" onClick={handleToggle}>
                        <MenuBookIcon fontSize="small" />
                        <span className="header-title">Recent Trades</span>
                        <span className="header-empty">No trades yet</span>
                    </div>
                    {webhookFeatureEnabled && (
                        <button
                            className="history-settings-btn"
                            onClick={handleSettingsClick}
                            title="Webhook Settings"
                        >
                            <SettingsIcon fontSize="small" />
                        </button>
                    )}
                </div>
                <TradeHistoryWebhookModal
                    open={webhookModalOpen}
                    onClose={() => setWebhookModalOpen(false)}
                    accounts={accounts}
                    selectedInstrument={selectedInstrument}
                    onAccountUpdate={loadAccounts}
                />
            </div>
        );
    }

    return (
        <div className={`trade-history-panel ${collapsed ? 'collapsed' : 'expanded'}`}>
            <div className="history-header-row">
                <div className="history-header" onClick={handleToggle}>
                    <MenuBookIcon fontSize="small" />
                    <span className="header-title">Trade History</span>
                    <div className="header-stats">
                        <span className={`stat-pl ${stats.totalPL >= 0 ? 'positive' : 'negative'}`}>
                            {formatPL(stats.totalPL)}
                        </span>
                        <span className="stat-winrate">
                            {stats.total > 0 ? Math.round((stats.wins / stats.total) * 100) : 0}% win
                        </span>
                    </div>
                    {collapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                </div>
                {webhookFeatureEnabled && (
                    <button
                        className="history-settings-btn"
                        onClick={handleSettingsClick}
                        title="Webhook Settings"
                    >
                        <SettingsIcon fontSize="small" />
                    </button>
                )}
            </div>

            {!collapsed && (
                <div className="history-content">
                    {/* Filter Bar */}
                    <div className="history-filter-bar">
                        {/* Period filter */}
                        <div className="filter-group period-filter">
                            <span
                                className={`filter-chip ${filters.period === 'today' ? 'active' : ''}`}
                                onClick={() => handleFilterChange('period', 'today')}
                            >
                                Today
                            </span>
                            <span
                                className={`filter-chip ${filters.period === '7d' ? 'active' : ''}`}
                                onClick={() => handleFilterChange('period', '7d')}
                            >
                                7d
                            </span>
                        </div>

                        {/* Account filter */}
                        {accounts.length > 0 && (
                            <div className="filter-group account-filter">
                                <span
                                    className={`filter-chip ${filters.accountId === 'all' ? 'active' : ''}`}
                                    onClick={() => handleFilterChange('accountId', 'all')}
                                >
                                    All
                                </span>
                                {accounts.map(acc => (
                                    <span
                                        key={acc.account_id}
                                        className={`filter-chip ${filters.accountId === String(acc.account_id) ? 'active' : ''}`}
                                        onClick={() => handleFilterChange('accountId', String(acc.account_id))}
                                        title={acc.name}
                                    >
                                        {acc.name.length > 8 ? acc.name.substring(0, 8) + '…' : acc.name}
                                    </span>
                                ))}
                            </div>
                        )}

                        {/* Source filter */}
                        {sourceIdsForPeriod.length > 0 && (
                            <div className="filter-group source-filter">
                                <SourceIcon style={{ fontSize: 12, opacity: 0.6 }} />
                                <span
                                    className={`filter-chip ${filters.sourceId === 'all' ? 'active' : ''}`}
                                    onClick={() => handleFilterChange('sourceId', 'all')}
                                >
                                    All
                                </span>
                                {sourceIdsForPeriod.map(src => (
                                    <span
                                        key={src}
                                        className={`filter-chip ${filters.sourceId === src ? 'active' : ''}`}
                                        onClick={() => handleFilterChange('sourceId', src)}
                                        title={src}
                                    >
                                        {src.length > 10 ? src.substring(0, 10) + '…' : src}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="history-entries">
                        {entries.length === 0 ? (
                            <div className="no-trades-message">
                                No trades found for selected filters
                            </div>
                        ) : entries.map((entry) => {
                            const validationData = parseValidationData(entry.lessons);
                            const isExpanded = expandedEntry === entry.entry_id;
                            const isWin = entry.profit_loss > 0;

                            return (
                                <div
                                    key={entry.entry_id}
                                    className={`history-entry ${isExpanded ? 'expanded' : ''}`}
                                >
                                    <div
                                        className="entry-row"
                                        onClick={() => setExpandedEntry(isExpanded ? null : entry.entry_id)}
                                    >
                                        <div className="entry-instrument">
                                            <span className="instrument-name">{entry.instrument}</span>
                                            <span className={`direction-tag ${entry.trade_type?.toLowerCase()}`}>
                                                {entry.trade_type === 'LONG' ? (
                                                    <TrendingUpIcon style={{ fontSize: 12 }} />
                                                ) : (
                                                    <TrendingDownIcon style={{ fontSize: 12 }} />
                                                )}
                                            </span>
                                            {entry.source_id && (
                                                <span className="source-tag" title={entry.source_id}>
                                                    {entry.source_id.length > 6 ? entry.source_id.substring(0, 6) + '…' : entry.source_id}
                                                </span>
                                            )}
                                        </div>
                                        <div className="entry-meta">
                                            <span className={`entry-pl ${isWin ? 'positive' : 'negative'}`}>
                                                {formatPL(entry.profit_loss)}
                                            </span>
                                            <span className="entry-date">{formatDate(entry.trade_date)}</span>
                                            <span className={`entry-outcome ${isWin ? 'win' : 'loss'}`}>
                                                {isWin ? 'WIN' : 'LOSS'}
                                            </span>
                                        </div>
                                        {isExpanded ? (
                                            <ExpandLessIcon fontSize="small" className="expand-icon" />
                                        ) : (
                                            <ExpandMoreIcon fontSize="small" className="expand-icon" />
                                        )}
                                    </div>

                                    {isExpanded && (
                                        <div className="entry-details">
                                            <div className="details-prices">
                                                <div className="price-col">
                                                    <span className="price-label">Entry</span>
                                                    <span className="price-val">{entry.entry_price}</span>
                                                </div>
                                                <div className="price-col">
                                                    <span className="price-label">Exit</span>
                                                    <span className="price-val">{entry.exit_price}</span>
                                                </div>
                                                {validationData?.tradeSetup?.desiredRr && (
                                                    <div className="price-col">
                                                        <span className="price-label">R:R</span>
                                                        <span className="price-val">
                                                            1:{validationData.tradeSetup.desiredRr.toFixed(1)}
                                                        </span>
                                                    </div>
                                                )}
                                                {entry.source_id && (
                                                    <div className="price-col">
                                                        <span className="price-label">Source</span>
                                                        <span className="price-val source-val">{entry.source_id}</span>
                                                    </div>
                                                )}
                                            </div>

                                            {validationData?.gates && validationData.gates.length > 0 && (
                                                <div className="details-gates">
                                                    <span className="gates-title">Validation Gates</span>
                                                    <div className="gates-list">
                                                        {validationData.gates.map((gate, idx) => (
                                                            <div
                                                                key={idx}
                                                                className={`gate-item ${gate.status?.toLowerCase()}`}
                                                            >
                                                                {gate.status === 'PASSED' ? (
                                                                    <CheckCircleIcon style={{ fontSize: 14 }} />
                                                                ) : (
                                                                    <CancelIcon style={{ fontSize: 14 }} />
                                                                )}
                                                                <span className="gate-name">{gate.name}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {entry.content && (
                                                <div className="details-notes">
                                                    <span className="notes-title">Notes</span>
                                                    <p className="notes-text">{entry.content}</p>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    {filteredEntries.length > limit && (
                        <button
                            className="view-all-btn"
                            onClick={onViewAll}
                        >
                            <span>View All {filteredEntries.length} Trades</span>
                            <OpenInNewIcon fontSize="small" />
                        </button>
                    )}
                </div>
            )}

            {/* Webhook Settings Modal */}
            <TradeHistoryWebhookModal
                open={webhookModalOpen}
                onClose={() => setWebhookModalOpen(false)}
                accounts={accounts}
                selectedInstrument={selectedInstrument}
                onAccountUpdate={loadAccounts}
            />
        </div>
    );
};

export default TradeHistoryPanel;
